import { Delete } from "@mui/icons-material";
import { Button, MenuItem, Select, SelectChangeEvent } from "@mui/material";
import React, { useEffect, useState } from "react";
import "./ProjectFontsAdmin.css";
import { useParams } from "react-router-dom";
import { ProjectFontDefinition } from "src/components/types";

function ProjectFontsAdmin(props: { reloadProjectFonts: () => void }) {
  const [fonts, setFonts] = useState<Array<ProjectFontDefinition>>([]);
  const [saveResult, setSaveResult] = useState<string>("");
  const params = useParams();
  const fontFaces = Array.from(document.fonts).sort(
    (a, b) => a.family.localeCompare(b.family) || a.weight.localeCompare(b.weight),
  );

  useEffect(() => {
    fetch(`/api/projects/${params.projectSlug}/fonts`)
      .then((response) => {
        if (response.ok) {
          return response.json();
        }
      })
      .then((data) => {
        if (data && data.fonts) {
          setFonts(data.fonts);
        }
      });
  }, [params.projectSlug]);

  function addProjectFont() {
    setFonts((prev) => {
      let projectSlug = params.projectSlug || "default";
      let maxIdNumber = Math.max(...prev.map((font) => parseInt(font.id.slice(projectSlug.length)) || 0), 0);
      let newId = projectSlug + (maxIdNumber + 1);
      return [
        ...prev,
        {
          id: newId,
          fontName: "font" + (maxIdNumber + 1),
          fontFamily: "",
          fontSize: "",
          fontWeight: "",
          fontStyle: "",
          fontColor: "",
          fontStrokeWidth: "",
          fontStrokeColor: "",
        },
      ];
    });
  }

  async function removeProjectFont(id: string) {
    try {
      const response = await fetch(`/api/projects/${params.projectSlug}/delete-font`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ id }),
      });
      if (response.ok) {
        setFonts((prev) => prev.filter((font) => font.id !== id));
      } else {
        setSaveResult("Error deleting font.");
        setTimeout(() => setSaveResult(""), 3000);
      }
    } catch (error) {
      console.error("Error deleting font:", error);
      setSaveResult("Error deleting font.");
      setTimeout(() => setSaveResult(""), 3000);
    }
  }

  function changeFontDef(id: string, e: React.ChangeEvent<HTMLInputElement>) {
    const { name, value } = e.target;
    setFonts((prev) =>
      prev.map((font) => {
        if (font.id === id) {
          return { ...font, [name]: value };
        }
        return font;
      }),
    );
  }

  function saveProjectFonts() {
    fetch(`/api/projects/${params.projectSlug}/fonts`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(fonts),
    })
      .then((response) => {
        if (response.ok) {
          setSaveResult("Project fonts saved successfully.");
          props.reloadProjectFonts();
          setTimeout(() => setSaveResult(""), 3000);
        }
      })
      .catch((error) => {
        console.error("Error saving project fonts:", error);
        setSaveResult("Error saving project fonts.");
        setTimeout(() => setSaveResult(""), 3000);
      });
  }

  function handleFontFaceChange(defId: string, event: SelectChangeEvent<unknown>) {
    const selectedIndex = event.target.value as number;
    const selectedFontFace = fontFaces[selectedIndex];
    setFonts((prev) =>
      prev.map((font) => {
        if (font.id === defId) {
          return {
            ...font,
            fontFamily: selectedFontFace.family,
            fontWeight: selectedFontFace.weight,
            fontStyle: selectedFontFace.style,
          };
        }
        return font;
      }),
    );
  }

  let fontElements = fonts.map((def) => (
    <div key={def.id} className="project-fonts-font-definition">
      <label htmlFor={`font-name-${def.id}`}>Name:</label>
      <input
        id={`font-name-${def.id}`}
        type="text"
        name="fontName"
        value={def.fontName}
        onChange={(e) => changeFontDef(def.id, e)}
      />
      <span style={{ alignSelf: "center" }}>Select:</span>
      <Select
        size="small"
        onChange={(e) => handleFontFaceChange(def.id, e)}
        value={fontFaces.findIndex(
          (f) => f.family === def.fontFamily && f.style === def.fontStyle && f.weight === def.fontWeight,
        )}
      >
        {fontFaces.map((fontFace, index) => (
          <MenuItem key={index} value={index}>
            <span style={{ fontFamily: fontFace.family, fontStyle: fontFace.style, fontWeight: fontFace.weight }}>
              {fontFace.family}
            </span>
          </MenuItem>
        ))}
      </Select>
      <label htmlFor={`font-family-${def.id}`}>Family:</label>
      <input
        id={`font-family-${def.id}`}
        type="text"
        name="fontFamily"
        value={def.fontFamily}
        onChange={(e) => changeFontDef(def.id, e)}
      />
      <label htmlFor={`font-weight-${def.id}`}>Weight:</label>
      <input
        id={`font-weight-${def.id}`}
        type="text"
        name="fontWeight"
        value={def.fontWeight}
        onChange={(e) => changeFontDef(def.id, e)}
      />
      <label htmlFor={`font-style-${def.id}`}>Style:</label>
      <input
        id={`font-style-${def.id}`}
        type="text"
        name="fontStyle"
        value={def.fontStyle}
        onChange={(e) => changeFontDef(def.id, e)}
      />
      <label htmlFor={`font-size-${def.id}`}>Size:</label>
      <input
        id={`font-size-${def.id}`}
        type="text"
        name="fontSize"
        value={def.fontSize}
        onChange={(e) => changeFontDef(def.id, e)}
      />
      <label htmlFor={`font-color-${def.id}`}>Color:</label>
      <input
        id={`font-color-${def.id}`}
        type="text"
        name="fontColor"
        value={def.fontColor}
        onChange={(e) => changeFontDef(def.id, e)}
      />
      <label htmlFor={`font-stroke-width-${def.id}`}>Stroke W:</label>
      <input
        id={`font-stroke-width-${def.id}`}
        type="text"
        name="fontStrokeWidth"
        value={def.fontStrokeWidth}
        onChange={(e) => changeFontDef(def.id, e)}
      />
      <label htmlFor={`font-stroke-color-${def.id}`}>Stroke C:</label>
      <input
        id={`font-stroke-color-${def.id}`}
        type="text"
        name="fontStrokeColor"
        value={def.fontStrokeColor}
        onChange={(e) => changeFontDef(def.id, e)}
      />
      <span
        className="project-fonts-font-preview"
        style={{
          fontFamily: def.fontFamily,
          fontSize: def.fontSize,
          fontWeight: def.fontWeight,
          fontStyle: def.fontStyle,
          color: def.fontColor,
          WebkitTextStrokeWidth: def.fontStrokeWidth,
          WebkitTextStrokeColor: def.fontStrokeColor,
          paintOrder: "stroke",
        }}
      >
        Font Preview
      </span>

      <button className="project-fonts-font-delete" onClick={() => removeProjectFont(def.id)}>
        <Delete />
      </button>
    </div>
  ));

  let fontList =
    fontElements.length > 0 ? (
      <div className="project-fonts-definition-list">{fontElements}</div>
    ) : (
      <p>No project fonts available.</p>
    );

  return (
    <div>
      {fontList}
      <div className="project-fonts-buttons">
        <Button variant="contained" color="primary" size="small" onClick={addProjectFont}>
          Add font
        </Button>
        <Button variant="contained" color="success" size="small" onClick={saveProjectFonts}>
          Save changes
        </Button>
        {saveResult && <span>{saveResult}</span>}
      </div>
    </div>
  );
}

export default ProjectFontsAdmin;
